import { BranchTag } from "@/components/BranchTag";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, PackageMinus, Eye, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { TabFilter } from "@/components/TabFilter";
import { TableToolbar } from "@/components/TableToolbar";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { QtyInput } from "@/components/StockQty";

import { fmtDate } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
const EMPTY_ITEM = { item_id: "", quantity: "" };

export default function GoodsIssues() {
  const { can } = usePermissions();
  const canCreateMovement = can("STOCK_MOVEMENTS", "create");
  const canPostMovement = can("STOCK_MOVEMENTS", "post");
  const { companies } = useCompany() || {};
  const [issues, setIssues] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const [ginRes, itemRes, whRes, projRes] = await Promise.all([
        api.get("/inventory/goods-issues"), api.get("/inventory/items"), api.get("/inventory/warehouses"), api.get("/projects"),
      ]);
      setIssues(ginRes.data); setItems(itemRes.data); setWarehouses(whRes.data); setProjects(projRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const tabs = [
    { key: "ALL",    label: "All Issues", count: issues.length },
    { key: "DRAFT",  label: "Draft",      count: issues.filter(g => g.status === "DRAFT").length },
    { key: "POSTED", label: "Posted",     count: issues.filter(g => g.status === "POSTED").length },
  ];

  const shown = useMemo(() => {
    let list = issues;
    if (activeTab !== "ALL") list = list.filter(g => g.status === activeTab);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(g =>
        (g.number || "").toLowerCase().includes(q) ||
        projectName(g.project_id).toLowerCase().includes(q)
      );
    }
    return list;
  }, [issues, activeTab, search, projects]);

  const post = async (gin) => {
    setBusyId(gin.id);
    try {
      await api.post(`/inventory/goods-issues/${gin.id}/post`);
      toast.success("Goods issue posted (Material Consumption)");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="goods-issues-page">
      <PageHeader title="Goods Issues" description="Material issued to a project. Posting debits Material Consumption and credits Inventory at average cost." action={canCreateMovement && <CreateGINDialog companies={companies} items={items} warehouses={warehouses} projects={projects} issues={issues} onCreated={load} />} />
      <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map(g => ({
          Number: g.number, Project: projectName(g.project_id),
          Date: g.issue_date, "Item Count": (g.items || []).length, Status: g.status,
        }))}
        csvFilename="goods-issues.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && issues.length === 0 ? (
          <EmptyState title="No goods issues yet" testId="goods-issues-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Project</TableHead><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((gin) => (
                <TableRow key={gin.id} data-testid={`gin-row-${gin.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{gin.number}<BranchTag row={gin} /></TableCell>
                  <TableCell className="text-foreground">
                    {projectName(gin.project_id)}
                    {gin.wbs_item_id ? <div className="text-[11px] text-muted-foreground">WBS linked</div> : <div className="text-[11px] text-amber-600 dark:text-amber-400">No WBS item</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(gin.issue_date)}</TableCell>
                  <TableCell className="max-w-xs text-xs text-muted-foreground">
                    {(gin.items || []).slice(0, 2).map((l) => {
                      const it = items.find((x) => x.id === l.item_id);
                      return <div key={l.id} className="truncate">{it ? it.name : "Item"} — {Number(l.quantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</div>;
                    })}
                    {(gin.items || []).length > 2 && <div>+ {gin.items.length - 2} more</div>}
                  </TableCell>
                  <TableCell><StatusPill value={gin.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <ViewGINDialog gin={gin} items={items} warehouses={warehouses} projectName={projectName} />
                    <Button size="sm" variant="ghost" title="Print issue slip" onClick={() => openPdf(`/print/goods-issues/${gin.id}`)} data-testid={`print-gin-${gin.number}`}><Printer className="h-3.5 w-3.5" /></Button>
                    {canPostMovement && gin.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === gin.id} onClick={() => post(gin)} data-testid={`post-gin-button-${gin.number}`}>
                        <PackageMinus className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                    <DocumentFlowButton docType="GOODS_ISSUE" docId={gin.id} cancelEndpoint={gin.status === "POSTED" ? `/inventory/goods-issues/${gin.id}/cancel` : undefined} onCancelled={load} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateGINDialog({ companies, items, warehouses, projects, issues = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", warehouse_id: "", project_id: "", wbs_item_id: "", issue_date: "" });
  const [lines, setLines] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const [wbsItems, setWbsItems] = useState([]);
  const [wbsState, setWbsState] = useState({ loading: false, error: "", groupsOnly: 0 });
  const [stock, setStock] = useState({}); // item_id -> { qty, avg_cost } in the chosen warehouse

  // Stock available in the chosen store, refreshed whenever the store changes or the dialog opens
  useEffect(() => {
    if (!open || !form.warehouse_id) { setStock({}); return; }
    let cancelled = false;
    api.get("/inventory/stock-balances", { params: { warehouse_id: form.warehouse_id } })
      .then(({ data }) => { if (!cancelled) setStock(Object.fromEntries((data || []).map((b) => [b.item_id, b]))); })
      .catch(() => { if (!cancelled) setStock({}); });
    return () => { cancelled = true; };
  }, [open, form.warehouse_id]);

  // Already issued to the chosen activity (posted issues), per item
  const issuedToActivity = useMemo(() => {
    const out = {};
    if (!form.wbs_item_id) return out;
    for (const g of issues) {
      if (g.status !== "POSTED" || g.wbs_item_id !== form.wbs_item_id) continue;
      for (const it of g.items || []) out[it.item_id] = (out[it.item_id] || 0) + Number(it.quantity || 0);
    }
    return out;
  }, [issues, form.wbs_item_id]);
  const unitOf = (itemId) => items.find((i) => i.id === itemId)?.uom_code || "";
  const qtyFmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

  // WBS activities (not group headers) of the chosen project, to charge the material to an activity
  useEffect(() => {
    if (!form.project_id) { setWbsItems([]); setWbsState({ loading: false, error: "", groupsOnly: 0 }); return; }
    let cancelled = false;
    setWbsState({ loading: true, error: "", groupsOnly: 0 });
    api.get(`/projects/${form.project_id}/wbs`)
      .then((res) => {
        if (cancelled) return;
        const all = Array.isArray(res.data) ? res.data : [];
        const activities = all.filter((w) => !w.is_group);
        setWbsItems(activities);
        setWbsState({ loading: false, error: "", groupsOnly: all.length - activities.length });
      })
      .catch((err) => {
        if (cancelled) return;
        setWbsItems([]);
        setWbsState({ loading: false, error: getErrorMessage(err), groupsOnly: 0 });
      });
    return () => { cancelled = true; };
  }, [form.project_id]);

  const wbsByCategory = useMemo(() => {
    const order = ["CONSTRUCTION", "PROCUREMENT", "ENGINEERING"];
    const groups = {};
    for (const w of wbsItems) {
      const key = w.category || "OTHER";
      if (!groups[key]) groups[key] = [];
      groups[key].push(w);
    }
    return Object.entries(groups).sort(([a], [b]) => order.indexOf(a) - order.indexOf(b));
  }, [wbsItems]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/goods-issues", { ...form, wbs_item_id: form.wbs_item_id || null, items: lines });
      toast.success("Goods issue created");
      setForm({ company_id: "", warehouse_id: "", project_id: "", wbs_item_id: "", issue_date: "" });
      setLines([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-gin-button"><Plus className="h-4 w-4" /> New Goods Issue</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Goods Issue</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-gin-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="gin-company-select" />
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="gin-warehouse-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Project</Label>
              <Select required value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v, wbs_item_id: "" })}>
                <SelectTrigger data-testid="gin-project-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="gin-date-input" value={form.issue_date} onChange={set("issue_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>WBS item <span className="font-normal text-muted-foreground">(the activity this material is used for)</span></Label>
            <Select value={form.wbs_item_id || "none"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "none" ? "" : v })} disabled={!form.project_id}>
              <SelectTrigger data-testid="gin-wbs-select" className="mt-1.5 [&>span]:truncate">
                <SelectValue placeholder={form.project_id ? "Select WBS item" : "Choose a project first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked to an activity</SelectItem>
                {wbsByCategory.map(([cat, list]) => (
                  <SelectGroup key={cat}>
                    <SelectLabel>{String(cat).charAt(0) + String(cat).slice(1).toLowerCase()}</SelectLabel>
                    {list.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}{w.uom ? ` (${w.uom})` : ""}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {wbsState.loading && <p className="mt-1 text-[11px] text-muted-foreground">Loading WBS items…</p>}
            {wbsState.error && <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">Could not load WBS items: {wbsState.error}</p>}
            {form.project_id && !wbsState.loading && !wbsState.error && wbsItems.length === 0 && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                {wbsState.groupsOnly
                  ? `This project has only ${wbsState.groupsOnly} group header(s) and no activities under them. Add activities in the project's WBS & Weightages page.`
                  : "This project has no WBS items yet. Add them in the project's WBS & Weightages page."}
              </p>
            )}
            {form.project_id && wbsItems.length > 0 && !form.wbs_item_id && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Without a WBS item the cost counts for the project, but not in Budget vs Actual per activity.</p>
            )}
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="space-y-1" data-testid={`gin-item-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <Select required value={l.item_id} onValueChange={(v) => updateLine(idx, "item_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`gin-item-select-${idx}`}><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent>
                      {items.filter((it) => !form.warehouse_id || it.category !== "SERVICE").map((it) => {
                        const b = stock[it.id];
                        return <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}{form.warehouse_id ? ` · in store: ${b ? qtyFmt(b.qty) : 0}` : ""}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <QtyInput required placeholder="Qty" className="w-24" unit={unitOf(l.item_id)} value={l.quantity} onChange={(v) => updateLine(idx, "quantity", v)} data-testid={`gin-item-qty-input-${idx}`} />
                  {lines.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
                {l.item_id && form.warehouse_id && (() => {
                  const available = Number(stock[l.item_id]?.qty || 0);
                  const sameItemOther = lines.reduce((s2, x, j) => (j !== idx && x.item_id === l.item_id ? s2 + Number(x.quantity || 0) : s2), 0);
                  const after = available - sameItemOther - Number(l.quantity || 0);
                  const unit = unitOf(l.item_id);
                  return (
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 pl-1 text-[11px]" data-testid={`gin-stock-info-${idx}`}>
                      <span className="text-muted-foreground">In store: <b className="font-mono text-foreground">{qtyFmt(available)}</b> {unit}</span>
                      <span className={after < 0 ? "font-medium text-rose-600 dark:text-rose-400" : after === 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}>
                        Left after this issue: <b className="font-mono">{qtyFmt(after)}</b> {unit}{after < 0 ? " — not enough stock" : ""}
                      </span>
                      {form.wbs_item_id && <span className="text-muted-foreground">Already issued to this activity: <b className="font-mono text-foreground">{qtyFmt(issuedToActivity[l.item_id] || 0)}</b> {unit}</span>}
                      {stock[l.item_id]?.avg_cost && Number(l.quantity) > 0 && <span className="text-muted-foreground">Value ≈ ₹{Number(stock[l.item_id].avg_cost * Number(l.quantity)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>}
                    </div>
                  );
                })()}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setLines([...lines, { ...EMPTY_ITEM }])} data-testid="add-gin-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || (form.warehouse_id && lines.some((l) => l.item_id && Number(l.quantity || 0) > 0 &&
              lines.filter((x) => x.item_id === l.item_id).reduce((a, x) => a + Number(x.quantity || 0), 0) > Number(stock[l.item_id]?.qty || 0)))} data-testid="create-gin-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ViewGINDialog({ gin, items, warehouses, projectName }) {
  const [open, setOpen] = useState(false);
  const [wbsName, setWbsName] = useState("");
  useEffect(() => {
    if (!open || !gin.wbs_item_id) return;
    api.get(`/projects/${gin.project_id}/wbs`).then(({ data }) => {
      const w = (data || []).find((x) => x.id === gin.wbs_item_id);
      setWbsName(w ? `${w.code} ${w.name}` : "");
    }).catch(() => {});
  }, [open, gin]);
  const fmtQ = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const lines = (gin.items || []).map((l) => {
    const it = items.find((x) => x.id === l.item_id);
    const net = Number(l.quantity) - Number(l.returned_qty || 0);
    const rate = Number(l.unit_cost || 0);
    return { ...l, name: it ? `${it.code} — ${it.name}` : "Item", net, rate, value: net * rate };
  });
  const total = lines.reduce((s, l) => s + l.value, 0);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" title="View" data-testid={`view-gin-${gin.number}`}><Eye className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2">Goods issue {gin.number} <StatusPill value={gin.status} /></DialogTitle></DialogHeader>
        <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div><span className="text-muted-foreground">Date: </span>{gin.issue_date}</div>
          <div><span className="text-muted-foreground">Project: </span>{projectName(gin.project_id)}</div>
          <div><span className="text-muted-foreground">From store: </span>{warehouses.find((w) => w.id === gin.warehouse_id)?.name || "—"}</div>
          <div><span className="text-muted-foreground">Activity: </span>{gin.wbs_item_id ? (wbsName || "…") : <span className="text-amber-600">Not linked</span>}</div>
          {gin.remarks && <div className="sm:col-span-2"><span className="text-muted-foreground">Remarks: </span>{gin.remarks}</div>}
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>
              <th className="p-2 text-left font-medium">Item</th><th className="p-2 text-right font-medium">Issued</th><th className="p-2 text-right font-medium">Returned</th>
              <th className="p-2 text-right font-medium">Net used</th><th className="p-2 text-right font-medium">Rate</th><th className="p-2 text-right font-medium">Value</th>
            </tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="p-2 text-foreground">{l.name}</td>
                  <td className="p-2 text-right font-mono text-xs">{fmtQ(l.quantity)}</td>
                  <td className="p-2 text-right font-mono text-xs">{Number(l.returned_qty) ? fmtQ(l.returned_qty) : "—"}</td>
                  <td className="p-2 text-right font-mono text-xs font-semibold">{fmtQ(l.net)}</td>
                  <td className="p-2 text-right font-mono text-xs">{l.rate ? inr(l.rate) : "—"}</td>
                  <td className="p-2 text-right font-mono text-xs">{l.rate ? inr(l.value) : "—"}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-semibold"><td className="p-2" colSpan={5}>Value charged to the project</td><td className="p-2 text-right font-mono text-xs">{gin.status === "POSTED" ? inr(total) : "— (draft)"}</td></tr>
            </tbody>
          </table>
        </div>
        {gin.status === "DRAFT" && <p className="text-xs text-amber-600 dark:text-amber-400">Draft — stock and project cost change only after you post it. Rates are fixed at posting (average cost).</p>}
        <div className="flex justify-end"><Button variant="outline" onClick={() => openPdf(`/print/goods-issues/${gin.id}`)}><Printer className="h-4 w-4" /> Print issue slip</Button></div>
      </DialogContent>
    </Dialog>
  );
}
